import {
  type Order,
  OrderFlagFillOrKill,
  OrderFlagImmediateOrCancel,
  OrderFlagPostOnly,
  type OrderFlags,
  OrderTypeOpenLong,
  OrderTypeOpenShort,
} from '@/api/spec';
import { MarginType } from '@/common/constants';
import type { MarketData } from '@/common/MarketData';
import { isPriceEditableOrder, isTriggerEditableOrder } from '@/feature/order_entry/order_editability';
import type { OrderFormData } from '@/feature/order_entry/order_schema';
import { OrderType, TiF } from '@/feature/order_entry/order_types';
import { getFormSideFromOrderType } from '@/feature/order_entry/utils';
import type { ViewOrder } from '@/feature/order_entry/ViewOrder';
import { mapOrderToViewOrder } from '../types/mapOrderToViewOrder';
import { leverageFromHundredths } from './leverageUtils';

/**
 * A native Change is Open*-only on both paths — `OperationHandler` reverts `CantChangeCloseOrder`.
 * Close* (TP/SL legs, reduce-only limit and stop orders) goes via cancel-and-repost instead.
 */
export function isApiChangeableOrder(order: Order): boolean {
  return order.t === OrderTypeOpenLong || order.t === OrderTypeOpenShort;
}

/**
 * On-chain Change edits a resting book order in place, so on top of the Open*-only rule it needs a
 * smart-contract id and excludes triggers, which rest engine-side rather than on the book.
 */
export function isOnChainChangeableOrder(order: Order): boolean {
  return isApiChangeableOrder(order) && !!order.scid && order.lp == null && !(order.tp != null && order.tp > 0);
}

export type OrderEditChanges = {
  price?: number;
  triggerPrice?: number;
  size?: number;
};

function parsePositive(raw: string): number | undefined {
  if (!raw.trim()) return undefined;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export function buildEditChangesFromForm(
  viewOrder: ViewOrder,
  form: { price: string; triggerPrice: string; size: string }
): OrderEditChanges {
  const changes: OrderEditChanges = {};

  if (isPriceEditableOrder(viewOrder)) {
    const price = parsePositive(form.price);
    if (price != null && price !== viewOrder.price) {
      changes.price = price;
    }
  }

  if (isTriggerEditableOrder(viewOrder)) {
    const triggerPrice = parsePositive(form.triggerPrice);
    if (triggerPrice != null && triggerPrice !== viewOrder.triggerPrice) {
      changes.triggerPrice = triggerPrice;
    }
  }

  const size = parsePositive(form.size);
  if (size != null && size !== viewOrder.size) {
    changes.size = size;
  }

  return changes;
}

/** Minimal OrderFormData for change-order toasts (price / size / side / type). */
export function buildChangeOrderNotificationData(
  order: Order,
  changes: OrderEditChanges,
  markets?: ReadonlyMap<number, MarketData>
): OrderFormData {
  const view = mapOrderToViewOrder(order, markets);
  const price = isTriggerEditableOrder(view)
    ? (changes.triggerPrice ?? view.triggerPrice ?? changes.price ?? view.price)
    : (changes.price ?? view.price);
  const size = changes.size ?? view.size;

  return {
    marketId: view.marketId,
    // Trade direction, not `view.side` (which is the position side and inverts Close* orders).
    side: getFormSideFromOrderType(order.t),
    reduceOnly: view.isClose,
    orderType: view.type,
    size: String(size ?? ''),
    price: price != null && price !== 0 ? String(price) : undefined,
  } as OrderFormData;
}

function tifFromFlags(flags: OrderFlags | undefined): TiF {
  if (flags == null) return TiF.gtc;
  if (flags & OrderFlagImmediateOrCancel) return TiF.immediateOrCancel;
  if (flags & OrderFlagFillOrKill) return TiF.fillOrKill;
  if (flags & OrderFlagPostOnly) return TiF.postOnly;
  return TiF.gtc;
}

/**
 * Full OrderFormData to repost a Close* order alongside a cancel of the original. Trigger
 * condition, position link, leverage and TiF have to be carried over — the engine can't infer them.
 */
export function buildRepostOrderFormData(
  order: Order,
  changes: OrderEditChanges,
  markets?: ReadonlyMap<number, MarketData>
): OrderFormData {
  const view = mapOrderToViewOrder(order, markets);
  const common = {
    marketId: view.marketId,
    side: getFormSideFromOrderType(order.t),
    marginType: MarginType.isolated, // ignored by buildApiOrder; matches mapPositionToCloseOrderFormData
    reduceOnly: view.isClose,
    size: String(changes.size ?? view.size),
    sizeUnit: 'asset' as const,
    orderSlider: 0,
    leverage: leverageFromHundredths(order.lv),
    triggerCondition: order.tpc,
    linkedPositionId: order.lp,
    tif: tifFromFlags(order.fl),
  };

  const limitPrice = changes.price ?? view.price;
  const triggerPrice = changes.triggerPrice ?? view.triggerPrice;

  if (triggerPrice != null) {
    // A trigger order with no limit price is a stop-market leg (buildApiOrder sends p=0 + slippage).
    return limitPrice
      ? { ...common, orderType: OrderType.stopLimit, stopPrice: String(triggerPrice), price: String(limitPrice) }
      : { ...common, orderType: OrderType.stopMarket, stopPrice: String(triggerPrice) };
  }

  return { ...common, orderType: OrderType.limit, price: String(limitPrice) };
}
